import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ChevronLeft, Check, X, Loader2, User, FileText, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { Link } from 'react-router';
import {
  Section, Button, Field, Input, Modal, StatusLine,
} from '../components/ui';
import supabase from '../lib/supabase';

type Coach = {
  id: string;
  user_id: string | null;
  display_name: string;
  bio: string | null;
  photo_url: string | null;
  type: string;
  verification_status: string;
  is_active: boolean;
  stripe_account_id: string | null;
  stripe_onboarding_completed_at: string | null;
  home_club_id: string | null;
  specialties: string[];
  languages: string[];
  years_experience: number | null;
  // Extended profile (PR: coach_profile_fields). All optional.
  title: string | null;
  title_other: string | null;
  gender: string | null;
  nationality: string | null;
  available_to_coach: string[];
  opposite_gender_policy: string | null;
  opposite_gender_policy_note: string | null;
  qualifications: string[];
  programs: string[];
  contact_email: string | null;
  contact_phone: string | null;
  booking_link: string | null;
};

const TITLE_LABELS: Record<string, string> = {
  tennis_coach: 'Tennis Coach',
  head_coach: 'Head Coach',
  junior_development_coach: 'Junior Development Coach',
  assistant_coach: 'Assistant Coach',
  performance_coach: 'Performance Coach',
};
const AUDIENCE_LABELS: Record<string, string> = {
  men: 'Men', women: 'Women', boys: 'Boys', girls: 'Girls',
  mixed_groups: 'Mixed groups', families: 'Families',
};
const PROGRAM_LABELS: Record<string, string> = {
  private_lessons: 'Private lessons',
  group_classes: 'Group classes',
  kids_classes: 'Kids classes',
  ladies_classes: 'Ladies classes',
  performance_training: 'Performance training',
};
const OPP_POLICY_LABELS: Record<string, string> = {
  available: 'Available for opposite-gender students',
  upon_request: 'Available upon request',
  not_available: 'Not available for opposite-gender students',
};
const GENDER_LABELS: Record<string, string> = {
  male: 'Male', female: 'Female', other: 'Other', prefer_not_to_say: '—',
};

function coachTitleLabel(c: Coach): string | null {
  if (!c.title) return null;
  if (c.title === 'other') return c.title_other?.trim() || null;
  return TITLE_LABELS[c.title] ?? null;
}

type Credential = {
  id: string;
  document_type: string;
  storage_path: string;
  ai_confidence: number | null;
  ai_reasoning: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_at: string | null;
  created_at: string;
};

export default function CoachDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [coach, setCoach] = useState<Coach | null>(null);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [acting, setActing] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    const [c, creds] = await Promise.all([
      supabase.from('coaches').select('*').eq('id', id).single(),
      supabase
        .from('coach_credentials')
        .select('*')
        .eq('coach_id', id)
        .order('created_at', { ascending: false }),
    ]);
    setCoach((c.data as Coach) ?? null);
    const list = (creds.data as Credential[]) ?? [];
    setCredentials(list);

    // Sign storage URLs for quick preview.
    const urls: Record<string, string> = {};
    for (const cr of list) {
      const { data } = await supabase.storage
        .from('identity-documents')
        .createSignedUrl(cr.storage_path, 600);
      if (data?.signedUrl) urls[cr.storage_path] = data.signedUrl;
    }
    setSignedUrls(urls);
  };

  useEffect(() => { load(); }, [id]);

  const act = async (credentialId: string, action: 'approve' | 'reject') => {
    setActing(credentialId);
    await supabase.functions.invoke('review-coach-credential', {
      body: { credentialId, action },
    });
    setActing(null);
    load();
  };

  if (!coach) return <div className="p-8 text-sm text-gray-500">Loading…</div>;

  return (
    <div className="p-8 max-w-4xl">
      <button
        onClick={() => nav('/coaches')}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
      >
        <ChevronLeft className="w-4 h-4" /> Back to coaches
      </button>

      <div className="flex items-start gap-4 mb-6">
        <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center overflow-hidden">
          {coach.photo_url ? (
            <img src={coach.photo_url} className="w-full h-full object-cover" />
          ) : <User className="w-9 h-9 text-gray-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl">{coach.display_name}</h1>
          <div className="text-sm text-gray-500 mt-0.5">
            {coachTitleLabel(coach) || (coach.type === 'freelance' ? 'Freelance coach' : 'Staff coach')}
            <span className="text-gray-400"> · {coach.type === 'freelance' ? 'Freelance' : 'Staff'}</span>
            {coach.years_experience ? ` · ${coach.years_experience}y` : ''}
            {coach.nationality ? ` · ${coach.nationality}` : ''}
            {coach.gender && coach.gender !== 'prefer_not_to_say' ? ` · ${GENDER_LABELS[coach.gender] ?? coach.gender}` : ''}
          </div>
          {coach.bio && <p className="text-sm mt-2">{coach.bio}</p>}
        </div>
        <Link
          to={`/coaches/${coach.id}/edit`}
          className="shrink-0 inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-[#2d5016] hover:bg-[#1a3010] text-white text-sm"
        >
          <Pencil className="w-3.5 h-3.5" /> Edit profile
        </Link>
      </div>

      <ProfilePanel coach={coach} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Info label="Verification" value={
          <span className={`text-xs px-2 py-1 rounded-full capitalize ${
            coach.verification_status === 'verified' ? 'bg-green-100 text-green-800'
            : coach.verification_status === 'rejected' ? 'bg-red-100 text-red-800'
            : 'bg-amber-100 text-amber-800'
          }`}>{coach.verification_status.replace('_', ' ')}</span>
        } />
        <Info label="Stripe Connect" value={
          coach.stripe_onboarding_completed_at ? (
            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">Connected</span>
          ) : coach.stripe_account_id ? (
            <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800">KYC pending</span>
          ) : (
            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">Not started</span>
          )
        } />
        <Info label="Active" value={coach.is_active ? 'Yes' : 'No'} />
      </div>

      <h2 className="text-lg mb-3">Credentials</h2>
      {credentials.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          No credentials uploaded yet.
        </div>
      ) : (
        <div className="space-y-3">
          {credentials.map((cr) => (
            <div key={cr.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start gap-3">
                {signedUrls[cr.storage_path] ? (
                  <a
                    href={signedUrls[cr.storage_path]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-28 h-20 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0"
                  >
                    <img src={signedUrls[cr.storage_path]} className="w-full h-full object-cover" />
                  </a>
                ) : (
                  <div className="w-28 h-20 rounded-lg bg-gray-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium capitalize">
                      {cr.document_type.replace('_', ' ')}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                      cr.status === 'approved' ? 'bg-green-100 text-green-800'
                      : cr.status === 'rejected' ? 'bg-red-100 text-red-800'
                      : 'bg-amber-100 text-amber-800'
                    }`}>{cr.status}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Confidence: {cr.ai_confidence == null ? '—' : (cr.ai_confidence * 100).toFixed(0) + '%'}
                    {' · '}uploaded {new Date(cr.created_at).toLocaleString()}
                  </div>
                  {cr.ai_reasoning && (
                    <p className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">{cr.ai_reasoning}</p>
                  )}
                </div>
                <div className="flex items-start gap-2 flex-shrink-0">
                  {signedUrls[cr.storage_path] && (
                    <a
                      href={signedUrls[cr.storage_path]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center"
                      title="Open document"
                    >
                      <ExternalLink className="w-4 h-4 text-gray-500" />
                    </a>
                  )}
                  {cr.status === 'pending' && (
                    <>
                      <button
                        onClick={() => act(cr.id, 'reject')}
                        disabled={acting === cr.id}
                        className="h-9 px-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm disabled:opacity-50 flex items-center gap-1"
                      >
                        {acting === cr.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                        Reject
                      </button>
                      <button
                        onClick={() => act(cr.id, 'approve')}
                        disabled={acting === cr.id}
                        className="h-9 px-3 rounded-lg bg-[#2d5016] hover:bg-[#1a3010] text-white text-sm disabled:opacity-50 flex items-center gap-1"
                      >
                        {acting === cr.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Approve
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <DeleteCoachSection coach={coach} onDeleted={() => nav('/coaches')} />
    </div>
  );
}

/**
 * Bottom-of-page Danger zone for irreversible coach deletion.
 * Guarded by the NO ACTION FKs (coach_bookings, stripe_transfers) so
 * the admin sees a clear reason instead of a raw Postgres error.
 * Requires typing the coach's display name to confirm.
 */
function DeleteCoachSection({
  coach, onDeleted,
}: { coach: Coach; onDeleted: () => void }) {
  const [counts, setCounts] = useState<{ coachBookings: number; transfers: number } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const [cb, tr] = await Promise.all([
        supabase.from('coach_bookings').select('id', { count: 'exact', head: true }).eq('coach_id', coach.id),
        supabase.from('stripe_transfers').select('id', { count: 'exact', head: true }).eq('coach_id', coach.id),
      ]);
      setCounts({ coachBookings: cb.count ?? 0, transfers: tr.count ?? 0 });
    })();
  }, [coach.id]);

  const hasBlockers = counts !== null
    && (counts.coachBookings > 0 || counts.transfers > 0);
  const confirmed = confirmText.trim() === coach.display_name.trim();

  const submit = async () => {
    if (!confirmed) return;
    setBusy(true); setErr(null);
    const { error } = await supabase.from('coaches').delete().eq('id', coach.id);
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onDeleted();
  };

  return (
    <>
      <div className="mt-8">
        <Section
          title={<span className="flex items-center gap-2 text-red-700"><Trash2 className="w-4 h-4" /> Danger zone</span>}
          subtitle="Deleting a coach removes their credentials, lesson types, hours, time off, affiliations, and session packages. Past bookings and Stripe transfers block the delete."
          className="border-red-200"
        >
          {counts === null ? (
            <div className="text-sm text-gray-500">Checking references…</div>
          ) : hasBlockers ? (
            <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-3">
              Cannot delete — still referenced by
              {counts.coachBookings > 0 && <> {counts.coachBookings} coach booking(s)</>}
              {counts.coachBookings > 0 && counts.transfers > 0 ? ',' : ''}
              {counts.transfers > 0 && <> {counts.transfers} Stripe transfer(s)</>}.
            </div>
          ) : (
            <Button variant="danger" icon={Trash2} onClick={() => { setConfirmText(''); setErr(null); setShowModal(true); }}>
              Delete this coach
            </Button>
          )}
        </Section>
      </div>

      {showModal && (
        <Modal title={`Delete ${coach.display_name}?`} onClose={() => setShowModal(false)}>
          <div className="space-y-3">
            <div className="text-sm text-gray-800">
              This permanently removes the coach and cascades to credentials, lesson types, hours, time off, affiliations, favorites, and session packages. <strong>This cannot be undone.</strong>
            </div>
            <Field label={`Type "${coach.display_name}" to confirm`}>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                autoFocus
              />
            </Field>
            {err && <StatusLine kind="error">{err}</StatusLine>}
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="secondary" className="flex-1" onClick={() => setShowModal(false)}>
                Keep the coach
              </Button>
              <Button type="button" variant="danger" className="flex-1" icon={Trash2}
                loading={busy} disabled={!confirmed || busy} onClick={submit}>
                Delete forever
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-1">{value}</div>
    </div>
  );
}

/**
 * Read-only admin view of the extended coach profile. Each field is
 * rendered only when populated so the panel hides itself entirely
 * for sparsely-filled coach rows.
 */
function ProfilePanel({ coach }: { coach: Coach }) {
  const audience = coach.available_to_coach ?? [];
  const programs = coach.programs ?? [];
  const specialties = coach.specialties ?? [];
  const languages = coach.languages ?? [];
  const qualifications = coach.qualifications ?? [];

  const hasAny =
    audience.length || programs.length || specialties.length || languages.length
    || qualifications.length || coach.opposite_gender_policy
    || coach.contact_email || coach.contact_phone || coach.booking_link;
  if (!hasAny) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 space-y-4">
      {coach.opposite_gender_policy && (
        <Row label="Opposite-gender policy">
          <span className="text-sm text-gray-900">
            {OPP_POLICY_LABELS[coach.opposite_gender_policy] ?? coach.opposite_gender_policy}
          </span>
          {coach.opposite_gender_policy_note && (
            <div className="text-xs text-gray-500 mt-0.5">{coach.opposite_gender_policy_note}</div>
          )}
        </Row>
      )}

      {audience.length > 0 && (
        <Row label="Coaches"><TagList items={audience} labels={AUDIENCE_LABELS} /></Row>
      )}
      {programs.length > 0 && (
        <Row label="Programs"><TagList items={programs} labels={PROGRAM_LABELS} /></Row>
      )}
      {specialties.length > 0 && (
        <Row label="Specialties"><TagList items={specialties} /></Row>
      )}
      {languages.length > 0 && (
        <Row label="Languages"><TagList items={languages} /></Row>
      )}
      {qualifications.length > 0 && (
        <Row label="Qualifications">
          <ul className="text-sm text-gray-900 space-y-0.5 list-disc pl-5">
            {qualifications.map((q, i) => <li key={`${q}-${i}`}>{q}</li>)}
          </ul>
        </Row>
      )}

      {(coach.contact_email || coach.contact_phone || coach.booking_link) && (
        <Row label="Contact">
          <div className="text-sm text-gray-900 space-y-0.5">
            {coach.contact_email && (
              <div><a className="text-[#2d5016] underline" href={`mailto:${coach.contact_email}`}>{coach.contact_email}</a></div>
            )}
            {coach.contact_phone && (
              <div><a className="text-[#2d5016] underline" href={`tel:${coach.contact_phone}`}>{coach.contact_phone}</a></div>
            )}
            {coach.booking_link && (
              <div className="truncate max-w-md">
                <a className="text-[#2d5016] underline" href={coach.booking_link} target="_blank" rel="noopener noreferrer">
                  {coach.booking_link}
                </a>
              </div>
            )}
          </div>
        </Row>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 items-start">
      <div className="text-xs text-gray-500 pt-0.5">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function TagList({ items, labels }: { items: string[]; labels?: Record<string, string> }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((x) => (
        <span key={x} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
          {labels?.[x] ?? x}
        </span>
      ))}
    </div>
  );
}
